import { apiService } from '../services/apiService';

export const APPLY_ENV_ALL_KEYS = '__all__';

/** Sentinel for "All Collections" / "All Requests" in apply-env scope dropdowns. */
export const APPLY_ENV_SCOPE_ALL = '__all__' as const;

export type ApplyEnvScopeAll = typeof APPLY_ENV_SCOPE_ALL;

export const AGENT_SELECT_MENU_MAX_HEIGHT_3_ITEMS = 114;

export const AGENT_BODY_REQUEST_METHODS = new Set(['POST', 'PUT', 'PATCH']);

export type ApplyEnvScopeId = number | ApplyEnvScopeAll;

export function normalizeAgentFormId(value: number | '' | string | null | undefined): number | '' {
  if (value === '' || value == null) return '';
  if (value === APPLY_ENV_SCOPE_ALL) return '';
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(parsed) ? '' : parsed;
}

export function agentFormIdsMatch(
  left: number | '' | string | null | undefined,
  right: number | '' | string | null | undefined,
): boolean {
  const a = normalizeAgentFormId(left);
  const b = normalizeAgentFormId(right);
  return a !== '' && b !== '' && a === b;
}

export async function fetchEnvironmentVariableKeys(environmentId: number): Promise<string[]> {
  const vars = await apiService.getEnvironmentVariables(environmentId);
  return vars
    .map((v) => v.key?.trim() ?? '')
    .filter((key) => key.length > 0);
}

export function resolveApplyEnvKeySelection(selectedKey: string): string {
  return selectedKey === APPLY_ENV_ALL_KEYS || !selectedKey.trim() ? '' : selectedKey.trim();
}

export function parseApplyEnvScopeSelectValue(raw: string): ApplyEnvScopeId | '' {
  if (raw === APPLY_ENV_SCOPE_ALL) return APPLY_ENV_SCOPE_ALL;
  if (raw === '') return '';
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? '' : parsed;
}

export function buildApplyEnvToRequestPayload(args: {
  projectId?: number | '';
  collectionId: ApplyEnvScopeId | '';
  requestId: ApplyEnvScopeId | '';
  environmentId: number;
  key?: string;
}): Record<string, unknown> {
  const data: Record<string, unknown> = {
    environmentId: args.environmentId,
  };
  if (args.projectId) {
    data.projectId = args.projectId;
  }
  if (args.collectionId !== '' && args.collectionId !== APPLY_ENV_SCOPE_ALL) {
    data.collectionId = args.collectionId;
  }
  if (args.requestId !== '' && args.requestId !== APPLY_ENV_SCOPE_ALL) {
    data.requestId = args.requestId;
  }
  if (args.key) {
    data.key = args.key;
  }
  return data;
}

export function buildApplyEnvToRequestUserMessage(args: {
  envName: string;
  collectionLabel: string;
  requestLabel: string;
  keyPhrase: string;
}): string {
  const { envName, collectionLabel, requestLabel, keyPhrase } = args;
  if (requestLabel !== 'All Requests') {
    return `Apply environment variables to request "${requestLabel}"${keyPhrase} in environment "${envName}".`;
  }
  if (collectionLabel !== 'All Collections') {
    return `Apply environment variables to all requests in collection "${collectionLabel}"${keyPhrase} in environment "${envName}".`;
  }
  return `Apply environment variables to all collections / all requests${keyPhrase} in environment "${envName}".`;
}

export function applyEnvCollectionLabel(
  collectionId: ApplyEnvScopeId | '',
  collections: Array<{ id: number; name: string }>,
): string {
  if (collectionId === APPLY_ENV_SCOPE_ALL || collectionId === '') return 'All Collections';
  return collections.find((c) => c.id === collectionId)?.name ?? `collection #${collectionId}`;
}

export function applyEnvRequestLabel(
  requestId: ApplyEnvScopeId | '',
  requests: Array<{ id: number; name: string }>,
): string {
  if (requestId === APPLY_ENV_SCOPE_ALL || requestId === '') return 'All Requests';
  return requests.find((r) => r.id === requestId)?.name ?? `request #${requestId}`;
}
